/**
 * Surfaces a toast when the user lands back from Stripe checkout with
 * `?stripe=success` or `?stripe=cancel` in the URL, and strips the flag
 * afterwards so a refresh doesn't re-trigger it.
 *
 * Mounted in Layout (covers /Settings, /Reviews, /MyBusiness, /Automations,
 * etc.) and in /Pricing (which has no Layout wrapper). With `return_path`
 * now passed dynamically to `create_checkout_session`, the redirect can
 * land on any of those pages — hence the global hook (decisiones §26.20).
 *
 * Side effects:
 *   - Success: invalidates ['gbusiness','me'] + ['billing','me'] so the SPA
 *     re-reads plan state without waiting for the parallel webhook.
 *   - URL cleanup uses `history.replaceState` so the back button doesn't
 *     bring the user back to a "?stripe=" URL.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { useLanguage } from '@/components/LanguageContext';
import { reconcileCheckout } from '@/api/billingService';


export default function useStripeRedirectToast() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const location = useLocation();
  // Guard against double-handling if React re-runs the effect for any
  // reason while the URL still contains the flag (StrictMode dev mode,
  // unrelated query refetches, etc.).
  const handledRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeFlag = params.get('stripe');
    if (!stripeFlag) {
      // Reset the guard when the user navigates to a flag-less URL so a
      // subsequent return from Stripe in the same SPA session still fires.
      handledRef.current = false;
      return;
    }
    if (handledRef.current) return;
    handledRef.current = true;

    // session_id (template {CHECKOUT_SESSION_ID} resuelto por Stripe) viene
    // en el success_url. Lo leemos ANTES de limpiar la URL.
    const sessionId = params.get('session_id');

    const invalidateBilling = () => {
      queryClient.invalidateQueries({ queryKey: ['gbusiness', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'me'] });
      // El webhook de invoice.created suele propagar 1-3s después del
      // redirect — invalidamos también la lista para que la factura del
      // trial / primer cargo aparezca al volver a /Settings (§26.21).
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
    };

    const cleanUrl = () => {
      params.delete('stripe');
      params.delete('session_id');
      const next = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (next ? `?${next}` : ''),
      );
    };

    if (stripeFlag === 'success') {
      toast.success(t('billing.subscription.checkoutSuccess'));
      // Reconciliación síncrona (red de seguridad si el webhook se pierde/
      // retrasa). Si hay session_id, sincronizamos el estado desde Stripe
      // ANTES de invalidar; el webhook sigue siendo el path primario, así
      // que ante cualquier fallo caemos al comportamiento de siempre
      // (invalidar y esperar al webhook). replaceState corre tras resolver
      // para que session_id siga legible durante la llamada.
      if (sessionId) {
        (async () => {
          try {
            await reconcileCheckout(sessionId);
          } catch (_) {
            // El webhook es el backstop — no rompemos la UX si esto falla.
          } finally {
            invalidateBilling();
            cleanUrl();
          }
        })();
      } else {
        invalidateBilling();
        cleanUrl();
      }
      return;
    } else if (stripeFlag === 'cancel') {
      toast(t('billing.subscription.checkoutCancel'));
    }

    cleanUrl();
  }, [location.search, queryClient, t]);
}
